import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { Input, InputField } from "@repo/components/src/ui/input";
import { AlertCircleIcon } from "lucide-react-native";
import { Controller, FieldValues } from "react-hook-form";
import { formatNumber } from "../../utils/numbers";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputProps } from "../types";

export type FormInputNumberProps<TFieldValues extends FieldValues> =
  FormInputProps<TFieldValues, number | undefined | null>;

export default function FormInputNumber<TFieldValues extends FieldValues>(
  props: FormInputNumberProps<TFieldValues>,
) {
  const {
    control,
    name,
    placeholder,
    label,
    required,
    disabled: disabledProp = false,
    className,
    size,
    labelSize = "md",
  } = props;
  return (
    <Controller
      name={name}
      control={control}
      disabled={disabledProp}
      render={({
        field: { onChange, value, ref, onBlur, disabled },
        fieldState: { error },
        formState: { isSubmitting },
      }) => {
        const formattedValue = value ? formatNumber(value.toString()) : "";
        return (
          <FormControl
            isInvalid={!!error}
            isRequired={required}
            isDisabled={disabled || isSubmitting}
            className={className}
            size={size}
          >
            {!!label && (
              <FormControlLabel>
                <FormControlLabelText size={labelSize}>
                  {label}
                </FormControlLabelText>
              </FormControlLabel>
            )}
            <Input
              isDisabled={disabled || isSubmitting}
              isInvalid={!!error}
              size={size}
            >
              <InputField
                ref={focusScrollRef(ref)}
                onBlur={onBlur}
                placeholder={placeholder}
                size={size}
                value={formattedValue}
                onChangeText={(newValue) => {
                  const cleanText = newValue.replace(/\D/g, "");
                  if (!cleanText) {
                    onChange(null);
                  } else {
                    onChange(Number(cleanText));
                  }
                }}
                keyboardType={"numeric"}
                textContentType={"none"}
                autoCapitalize="none"
              />
            </Input>
            <FormControlError>
              <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
              <FormControlErrorText>{error?.message}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
